import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Download } from 'lucide-react';

const ExcelUpload = ({ 
  onDataParsed, 
  expectedColumns, 
  templateData,
  templateFileName,
  title = "Upload Excel File"
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setPreviewData(null);

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(selectedFile.type) && 
        !selectedFile.name.endsWith('.xlsx') && 
        !selectedFile.name.endsWith('.xls') &&
        !selectedFile.name.endsWith('.csv')) {
      setError('Please upload a valid Excel file (.xlsx, .xls) or CSV file');
      return;
    }

    // Parse the file
    parseFile(selectedFile);
  };

  const parseFile = (file) => {
    setIsProcessing(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        
        if (jsonData.length === 0) {
          setError('The uploaded file is empty');
          setIsProcessing(false);
          return;
        }

        // Validate columns if expectedColumns provided
        if (expectedColumns && expectedColumns.length > 0) {
          const fileColumns = Object.keys(jsonData[0]);
          const missingColumns = expectedColumns.filter(col => 
            !fileColumns.some(fc => fc.toLowerCase() === col.toLowerCase())
          );
          
          if (missingColumns.length > 0) {
            setError(`Missing required columns: ${missingColumns.join(', ')}`);
            setIsProcessing(false);
            return;
          }
        }

        setPreviewData(jsonData);
        setIsProcessing(false);
      } catch (err) {
        setError('Failed to parse the file. Please check the format.');
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read the file');
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleUpload = () => {
    if (previewData && onDataParsed) {
      onDataParsed(previewData);
      handleClose();
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setFile(null);
    setPreviewData(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    if (!templateData || templateData.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, templateFileName || 'template.xlsx');
  };

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="outline"
        className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0]"
        data-testid="upload-excel-button"
      >
        <Upload className="w-4 h-4 mr-2" />
        Upload Excel
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#E2E8F0] flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#22C55E]" />
              {title}
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Upload an Excel file (.xlsx, .xls) or CSV to import data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Download Template Button */}
            {templateData && (
              <div className="flex items-center justify-between p-3 bg-[#0F172A] rounded-lg border border-[#334155]">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-[#2563EB]" />
                  <span className="text-sm text-[#94A3B8]">Download template to see the expected format</span>
                </div>
                <Button
                  onClick={downloadTemplate}
                  variant="ghost"
                  size="sm"
                  className="text-[#2563EB] hover:text-[#60A5FA] hover:bg-[#2563EB]/10"
                >
                  Download Template
                </Button>
              </div>
            )}

            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                file ? 'border-[#22C55E] bg-[#22C55E]/5' : 'border-[#334155] hover:border-[#475569]'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="excel-file-input"
              />
              <label
                htmlFor="excel-file-input"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  file ? 'bg-[#22C55E]/20' : 'bg-[#334155]'
                }`}>
                  <FileSpreadsheet className={`w-6 h-6 ${file ? 'text-[#22C55E]' : 'text-[#64748B]'}`} />
                </div>
                {file ? (
                  <div>
                    <p className="text-[#E2E8F0] font-medium">{file.name}</p>
                    <p className="text-sm text-[#64748B]">Click to change file</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-[#E2E8F0]">Click to select a file</p>
                    <p className="text-sm text-[#64748B]">or drag and drop</p>
                  </div>
                )}
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#EF4444]">{error}</p>
              </div>
            )}

            {/* Processing Indicator */}
            {isProcessing && (
              <div className="text-center py-4">
                <div className="animate-spin w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-sm text-[#94A3B8]">Processing file...</p>
              </div>
            )}

            {/* Preview */}
            {previewData && !isProcessing && (
              <div className="bg-[#0F172A] rounded-lg border border-[#334155] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span className="text-[#E2E8F0] font-medium">
                    {previewData.length} row(s) found
                  </span>
                </div>
                
                {/* Preview Table */}
                <div className="overflow-x-auto max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#334155]">
                        {Object.keys(previewData[0]).slice(0, 5).map((key, idx) => (
                          <th key={idx} className="text-left py-2 px-2 text-[#94A3B8] font-medium">
                            {key}
                          </th>
                        ))}
                        {Object.keys(previewData[0]).length > 5 && (
                          <th className="text-left py-2 px-2 text-[#64748B]">...</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 3).map((row, rowIdx) => (
                        <tr key={rowIdx} className="border-b border-[#334155]/50">
                          {Object.values(row).slice(0, 5).map((val, colIdx) => (
                            <td key={colIdx} className="py-2 px-2 text-[#E2E8F0] truncate max-w-[150px]">
                              {String(val)}
                            </td>
                          ))}
                          {Object.keys(row).length > 5 && (
                            <td className="py-2 px-2 text-[#64748B]">...</td>
                          )}
                        </tr>
                      ))}
                      {previewData.length > 3 && (
                        <tr>
                          <td colSpan={6} className="py-2 px-2 text-[#64748B] text-center">
                            ... and {previewData.length - 3} more row(s)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Expected Columns Info */}
            {expectedColumns && (
              <div className="text-xs text-[#64748B]">
                <span className="font-medium">Expected columns: </span>
                {expectedColumns.join(', ')}
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!previewData || isProcessing}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
              data-testid="confirm-upload-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import {previewData?.length || 0} Row(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExcelUpload;
